import React, { useEffect, useState } from 'react';
import axios from 'axios';

// 날짜 → 'YYYY-MM-DD'
const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// 일정 종류별 색상
//  deposit(계약금)은 캘린더에 표시하지 않으므로 색 없음
const TYPE_INFO = {
  middle:  { color: '#2196F3', label: '중도금' },
  balance: { color: '#4CAF50', label: '잔금' },
  expiry:  { color: '#f44336', label: '임차만료' },
  payment: { color: '#9E9E9E', label: '기타' },
};
const typeColor = (t) => (TYPE_INFO[t] || TYPE_INFO.payment).color;
const typeLabel = (t) => (TYPE_INFO[t] || TYPE_INFO.payment).label;

// 캘린더에 표시할 일정만 남긴다 (계약금 제외)
const isVisible = (t) => t !== 'deposit';

// 소재지를 이미지 파일명과 같은 규칙으로 축약
//  "서울특별시 관악구 봉천동 14-7 104호" + dong="봉천동" → "봉천동_14-7_104호"
const shortAddr = (full, dong) => {
  let name = String(full || '').trim();
  if (dong) {
    const i = name.indexOf(dong);
    if (i >= 0) name = name.slice(i);
  }
  return name.replace(/\s+/g, '_');
};

// 최종 일정명: "잔금_봉천동_14-7_104호"
const eventLabel = (ev) =>
  `${typeLabel(ev.event_type)}_${shortAddr(ev.location_full_address, ev.location_dong)}`;

export default function ContractCalendar() {
  // 현재 보고 있는 달의 1일
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // 달이 바뀔 때마다 해당 월 일정 조회
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
        const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
        const res = await axios.get('/api/contracts/schedules/all', {
          params: { from: ymd(first), to: ymd(last) },
        });
        if (res.data.success) setEvents(res.data.data.filter(e => isVisible(e.event_type)));
      } catch (e) {
        console.error('일정 조회 실패:', e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [cursor]);

  // 달력 격자 구성 (앞뒤 빈칸 포함)
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();   // 0=일
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // 날짜별 일정 묶기
  const byDate = {};
  events.forEach((ev) => {
    const key = String(ev.event_date).split('T')[0];
    if (!byDate[key]) byDate[key] = [];
    byDate[key].push(ev);
  });

  const todayStr = ymd(new Date());

  const moveMonth = (diff) => {
    setCursor(new Date(year, month + diff, 1));
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>📅 계약 일정</h1>
        <button
          onClick={() => (window.location.href = '/contracts/list')}
          style={{
            backgroundColor: '#757575', color: 'white', border: 'none',
            padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
          }}
        >
          📋 목록으로
        </button>
      </div>

      {/* 월 이동 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '15px' }}>
        <button onClick={() => moveMonth(-1)} style={navBtn}>◀</button>
        <h2 style={{ margin: 0, minWidth: '160px', textAlign: 'center' }}>
          {year}년 {month + 1}월
        </h2>
        <button onClick={() => moveMonth(1)} style={navBtn}>▶</button>
        <button
          onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); }}
          style={{ ...navBtn, width: 'auto', padding: '0 12px', fontSize: '13px' }}
        >
          오늘
        </button>
      </div>

      {/* 범례 */}
      <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginBottom: '12px', fontSize: '12px' }}>
        <span><span style={dot('#2196F3')} /> 중도금</span>
        <span><span style={dot('#4CAF50')} /> 잔금</span>
        <span><span style={dot('#f44336')} /> 임차 만료</span>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '10px', color: '#888' }}>불러오는 중...</div>}

      {/* 요일 헤더 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '4px' }}>
        {['일', '월', '화', '수', '목', '금', '토'].map((w, i) => (
          <div key={w} style={{
            textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '13px',
            color: i === 0 ? '#f44336' : i === 6 ? '#2196F3' : '#333',
            backgroundColor: '#f5f5f5', borderRadius: '4px',
          }}>
            {w}
          </div>
        ))}
      </div>

      {/* 날짜 격자 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '4px' }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} style={{ minHeight: '96px' }} />;

          const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const list = byDate[key] || [];
          const isToday = key === todayStr;

          return (
            <div
              key={key}
              style={{
                minHeight: '96px', padding: '5px',
                minWidth: 0, overflow: 'hidden',
                border: isToday ? '2px solid #4CAF50' : '1px solid #eee',
                borderRadius: '4px', backgroundColor: '#fff',
              }}
            >
              <div style={{
                fontSize: '12px', fontWeight: isToday ? 'bold' : 'normal',
                color: i % 7 === 0 ? '#f44336' : i % 7 === 6 ? '#2196F3' : '#333',
                marginBottom: '4px',
              }}>
                {d}
              </div>

              {/* 일정 클릭 → 해당 계약 상세보기로 이동 */}
              {list.map((ev) => (
                <div
                  key={ev.schedule_id}
                  onClick={() => (window.location.href = `/contracts/${ev.contract_id}`)}
                  title={`${eventLabel(ev)}\n${ev.location_full_address || ''}`}
                  style={{
                    fontSize: '10px', padding: '3px 4px', marginBottom: '3px',
                    backgroundColor: typeColor(ev.event_type), color: 'white',
                    borderRadius: '3px', cursor: 'pointer',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    maxWidth: '100%', boxSizing: 'border-box',
                  }}
                >
                  {eventLabel(ev)}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* 이번 달 일정 목록 */}
      <div style={{ marginTop: '25px' }}>
        <h3 style={{ fontSize: '15px' }}>이번 달 일정 ({events.length}건)</h3>
        {events.length === 0 ? (
          <div style={{ color: '#999', fontSize: '13px', padding: '10px' }}>등록된 일정이 없습니다.</div>
        ) : (
          events.map((ev) => (
            <div
              key={ev.schedule_id}
              onClick={() => (window.location.href = `/contracts/${ev.contract_id}`)}
              style={{
                padding: '10px', marginBottom: '6px', borderRadius: '4px',
                backgroundColor: '#fafafa', cursor: 'pointer',
                borderLeft: `4px solid ${typeColor(ev.event_type)}`,
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: 'bold' }}>
                {String(ev.event_date).split('T')[0]} · {eventLabel(ev)}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>
                {ev.location_full_address}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const navBtn = {
  width: '40px', height: '36px', border: '1px solid #ddd',
  backgroundColor: '#fff', borderRadius: '4px', cursor: 'pointer', fontSize: '15px',
};

const dot = (c) => ({
  display: 'inline-block', width: '10px', height: '10px',
  backgroundColor: c, borderRadius: '2px', marginRight: '4px',
});
